import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Clock, Spinner } from "@phosphor-icons/react";
import { api, brl, getUser, saveAuth, errMsg } from "@/lib/api";
import { PIX_TYPES, formatByType } from "@/lib/pix";
import { useFeeRates, fmtPct } from "@/hooks/useFeeRates";

export default function Withdraw() {
  const [pixType, setPixType] = useState("cpf");
  const [form, setForm] = useState({ amount: "", pix_key: "", beneficiary_name: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const user = getUser();
  const { rates } = useFeeRates();
  const rate = rates.withdraw_fee_rate || 0;
  const total = Number(form.amount || 0);
  const fee = total * rate;
  const currentPh = PIX_TYPES.find(p => p.value === pixType)?.ph || "";

  const handlePixChange = (e) => {
    setForm({ ...form, pix_key: formatByType(pixType, e.target.value) });
  };
  const changePixType = (t) => {
    setPixType(t);
    setForm({ ...form, pix_key: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/withdraw", {
        pix_key: form.pix_key,
        pix_key_type: pixType,
        amount: Number(form.amount),
        beneficiary_name: form.beneficiary_name,
      });
      try {
        const me = await api.get("/auth/me");
        saveAuth(localStorage.getItem("vb_token"), { ...user, ...me.data });
      } catch (e) { console.warn("balance refresh failed:", e?.message || e); }
      setResult(data);
      if (data.status === "approved") toast.success("Pix enviado com sucesso");
      else toast.info("Solicitação registrada");
    } catch (err) {
      toast.error(errMsg(err, "Erro ao processar saque"));
    } finally { setLoading(false); }
  };

  const inp = "w-full bg-white/[0.02] border border-white/[0.08] rounded-xl px-4 py-3 focus:border-[var(--accent)] outline-none transition-colors";

  if (result) {
    const isApproved = result.status === "approved";
    return (
      <div className="fadeUp max-w-2xl">
        <div className="app-card text-center py-10" data-testid="withdraw-result">
          {isApproved ? <CheckCircle size={64} color="var(--success)" weight="fill" className="mx-auto" /> : <Clock size={64} color="var(--accent)" weight="fill" className="mx-auto" />}
          <div className="font-display font-black text-fluid-h3 mt-4" data-testid="withdraw-result-title">
            {isApproved ? "Pix enviado!" : "Solicitação recebida"}
          </div>
          <div className="font-display font-black text-fluid-h2 text-[var(--accent)] my-4 tabular-nums">{brl(result.amount)}</div>
          <p className="text-white/70 mb-4 wrap-safe" data-testid="withdraw-message">{result.message}</p>
          <div className="border border-white/[0.08] bg-white/[0.02] rounded-xl p-4 text-left text-sm space-y-1 max-w-md mx-auto">
            <div className="flex justify-between gap-2"><span className="text-white/60">Chave Pix:</span><span className="font-mono truncate">{result.pix_key}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Tipo:</span><span className="uppercase font-mono text-xs">{result.pix_key_type}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Taxa:</span><span className="tabular-nums">{brl(result.fee)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Protocolo:</span><span className="font-mono text-xs">{result.id?.slice(0, 8)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Status:</span>
              <span className={isApproved ? "text-[var(--success)]" : "text-[var(--accent)]"}>{isApproved ? "APROVADO" : "PROCESSANDO"}</span>
            </div>
          </div>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <button onClick={() => { setResult(null); setForm({ amount: "", pix_key: "", beneficiary_name: "" }); }} className="btn-secondary" data-testid="withdraw-again">Novo saque</button>
            <a href="/app" className="btn-primary" data-testid="withdraw-back">Voltar ao dashboard</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fadeUp max-w-3xl">
      <div className="page-header">
        <div className="label-caps mb-2">Saída</div>
        <h1 className="page-title wrap-safe">Sacar via Pix</h1>
        <p className="page-subtitle wrap-safe">Envie o dinheiro para qualquer chave Pix.</p>
      </div>

      <div className="app-card">
        <div className="mb-6">
          <div className="label-caps">Seu saldo</div>
          <div className="font-display font-black text-fluid-h2 text-[var(--accent)] tabular-nums leading-none">{brl(user?.balance || 0)}</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-2">Nome completo do favorecido</label>
            <input required value={form.beneficiary_name || ""} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} data-testid="withdraw-beneficiary" className={inp} placeholder="Nome como está na conta destino" disabled={loading} />
          </div>
          <div>
            <label className="label-caps block mb-2">Tipo da chave</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {PIX_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => changePixType(t.value)}
                  data-testid={`pix-type-${t.value}`}
                  className={`text-xs py-2.5 border rounded-lg transition-all ${
                    pixType === t.value
                      ? "bg-[var(--accent)] text-black font-bold border-[var(--accent)] shadow-[0_4px_14px_-4px_rgba(255,179,15,0.5)]"
                      : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-caps block mb-2">Chave Pix</label>
            <input required value={form.pix_key} onChange={handlePixChange} data-testid="withdraw-pixkey" className={`${inp} font-mono`} placeholder={currentPh} disabled={loading} inputMode={["cpf","cnpj","phone"].includes(pixType) ? "numeric" : "text"} />
          </div>
          <div>
            <label className="label-caps block mb-2">Valor</label>
            <div className="flex items-center gap-3 border border-white/[0.08] bg-white/[0.02] rounded-xl px-4 py-3 focus-within:border-[var(--accent)] transition-colors">
              <span className="text-white/60 font-mono">R$</span>
              <input
                type="number" step="0.01" min="1" required
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="withdraw-amount"
                className="flex-1 bg-transparent outline-none font-display text-2xl font-bold min-w-0"
                placeholder="0,00" disabled={loading}
              />
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-4 space-y-1.5 text-sm" data-testid="withdraw-fee-preview">
            <div className="flex justify-between"><span className="text-white/60">Valor</span><span className="tabular-nums">{brl(total)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Taxa {fmtPct(rate)}</span><span className="text-[var(--accent)] tabular-nums">{brl(fee)}</span></div>
            <div className="flex justify-between font-bold pt-2 border-t border-white/[0.06]"><span>Total debitado</span><span className="text-[var(--danger)] tabular-nums">{brl(total + fee)}</span></div>
          </div>

          {loading && (
            <div className="border border-[var(--accent)]/40 bg-[var(--accent)]/5 rounded-xl p-4 flex items-center gap-3" data-testid="withdraw-loading">
              <Spinner size={22} color="var(--accent)" className="animate-spin shrink-0" />
              <div className="text-sm text-white/80">
                <b>Enviando Pix pelo Mercado Pago…</b>
                <div className="text-xs text-white/60 mt-1">Não feche esta janela.</div>
              </div>
            </div>
          )}

          <button disabled={loading} data-testid="withdraw-submit" className="btn-primary w-full">
            {loading ? (<><Spinner size={16} className="animate-spin" /> Processando…</>) : "Confirmar saque"}
          </button>
        </form>
      </div>
    </div>
  );
}
